import { Stack, Group, TextInput, ActionIcon, Radio, Button, Text, Box } from '@mantine/core';
import { IconTrash, IconPlus, IconPhone, IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from './PhoneInput';

export interface PhoneItem { label: string; number: string; isPrimary: boolean }
export interface AddressItem { label: string; address: string; isPrimary: boolean }

const PHONE_PRESETS = ['Doimiy', 'Ish', 'Uy', 'Ota', 'Ona', 'Aka', 'Opa', "Qo'shimcha"];
const ADDR_PRESETS = ['Yashash', "Ro'yxatdan o'tgan", "Qo'shimcha"];

/** Ko'p telefon tahrirlagich — har biri label + raqam, ichidan primary tanlanadi. */
export function MultiPhoneEditor({ value, onChange }: { value: PhoneItem[]; onChange: (v: PhoneItem[]) => void }) {
  const { t } = useTranslation();
  const items = value ?? [];

  const setItem = (i: number, patch: Partial<PhoneItem>) => {
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const setPrimary = (i: number) => onChange(items.map((it, idx) => ({ ...it, isPrimary: idx === i })));
  const add = () => onChange([...items, { label: PHONE_PRESETS[Math.min(items.length, PHONE_PRESETS.length - 1)], number: '', isPrimary: items.length === 0 }]);
  const remove = (i: number) => {
    const next = items.filter((_, idx) => idx !== i);
    if (next.length && !next.some((x) => x.isPrimary)) next[0].isPrimary = true;
    onChange(next);
  };

  return (
    <Box>
      <Group gap={6} mb={6}><IconPhone size={16} /><Text size="sm" fw={600}>{t('multiContact.phones')}</Text></Group>
      <Stack gap="xs">
        {items.map((it, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <TextInput placeholder={t('multiContact.labelPlaceholder')} w={110} value={it.label} onChange={(e) => setItem(i, { label: e.currentTarget.value })} />
            <PhoneInput style={{ flex: 1 }} value={it.number} onChange={(num) => setItem(i, { number: num })} />
            <Radio checked={it.isPrimary} onChange={() => setPrimary(i)} title={t('multiContact.primary')} mt={10} />
            <ActionIcon color="red" variant="subtle" mt={6} onClick={() => remove(i)}><IconTrash size={16} /></ActionIcon>
          </Group>
        ))}
      </Stack>
      <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} mt="xs" onClick={add}>{t('multiContact.addPhone')}</Button>
      {items.length > 0 && <Text size="xs" c="dimmed" mt={4}>{t('multiContact.primaryHint')}</Text>}
    </Box>
  );
}

/** Ko'p manzil tahrirlagich. */
export function MultiAddressEditor({ value, onChange }: { value: AddressItem[]; onChange: (v: AddressItem[]) => void }) {
  const { t } = useTranslation();
  const items = value ?? [];
  const setItem = (i: number, patch: Partial<AddressItem>) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const setPrimary = (i: number) => onChange(items.map((it, idx) => ({ ...it, isPrimary: idx === i })));
  const add = () => onChange([...items, { label: ADDR_PRESETS[Math.min(items.length, ADDR_PRESETS.length - 1)], address: '', isPrimary: items.length === 0 }]);
  const remove = (i: number) => {
    const next = items.filter((_, idx) => idx !== i);
    if (next.length && !next.some((x) => x.isPrimary)) next[0].isPrimary = true;
    onChange(next);
  };

  return (
    <Box>
      <Group gap={6} mb={6}><IconMapPin size={16} /><Text size="sm" fw={600}>{t('multiContact.addresses')}</Text></Group>
      <Stack gap="xs">
        {items.map((it, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <TextInput placeholder={t('multiContact.labelShort')} w={110} value={it.label} onChange={(e) => setItem(i, { label: e.currentTarget.value })} />
            <TextInput style={{ flex: 1 }} placeholder={t('multiContact.address')} value={it.address} onChange={(e) => setItem(i, { address: e.currentTarget.value })} />
            <Radio checked={it.isPrimary} onChange={() => setPrimary(i)} mt={10} />
            <ActionIcon color="red" variant="subtle" mt={6} onClick={() => remove(i)}><IconTrash size={16} /></ActionIcon>
          </Group>
        ))}
      </Stack>
      <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} mt="xs" onClick={add}>{t('multiContact.addAddress')}</Button>
    </Box>
  );
}
